import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
    Hash,
    Briefcase,
    User,
    Calendar,
    FileText,
    MapPin,
    Globe,
    Flag,
    Navigation,
    DollarSign,
    Clock,
    AlertCircle,
    Save,
    Loader2,
    PlusSquare,
    ChevronRight,
    Shield,
    Upload,
    Info,
    CheckCircle2,
    Check,
    ArrowDownRight,
    Minus,
    ArrowUpRight,
    IndianRupee,
    X
} from 'lucide-react';
import styles from './ProjectForm.module.css';
import api from '../../api/axios';
import toast from 'react-hot-toast';
import CustomDatePicker from '../../components/Common/DatePicker/CustomDatePicker';
import CustomSelect from '../../components/Common/Select/CustomSelect';
import NotFound from '../Error/NotFound';
import Swal from 'sweetalert2';
import { Plus } from 'lucide-react';

const ProjectForm = ({ project = null, onSave, onCancel }) => {
    const [formData, setFormData] = useState({
        project_code: '',
        project_name: '',
        project_type_id: '',
        customer_id: '',
        work_order_date: '',
        material_submission_date: '',
        commissioning_date: '',
        status: 'planning',
        priority: 'medium',
        total_sales_value: '',
        delay_days: 0,
        project_summary: '',
        site_address: '',
        city_id: '',
        state_id: '',
        country_id: '',
        contact_number: '',
        site_supervisor_id: '',
        project_manager_id: '',
        site_google_map_link: '',
        site_latitude: '',
        site_longitude: '',
        is_design_approval_enable: false,
        is_inspection_tracking_enable: false,
        is_material_tracking_enable: false,
        is_client_billing_enable: false,
        radius: '',
        radius_unit_id: '',
    });

    const [projectTypes, setProjectTypes] = useState([]);
    const [files, setFiles] = useState({
        contract_copy: null,
        technical_specification: null,
        boq: null
    });
    const [customers, setCustomers] = useState([]);
    const [countries, setCountries] = useState([]);
    const [states, setStates] = useState([]);
    const [cities, setCities] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [units, setUnits] = useState([]);
    const [loading, setLoading] = useState(false);
    const [fetchingProject, setFetchingProject] = useState(false);
    const [notFound, setNotFound] = useState(false);

    const [isCustomerModalOpen, setIsCustomerModalOpen] = useState(false);
    const [newCustomer, setNewCustomer] = useState({ customer_name: '', mobile: '', address: '', gst: '' });
    
    const [isProjectTypeModalOpen, setIsProjectTypeModalOpen] = useState(false);
    const [newProjectType, setNewProjectType] = useState({ type_name: '' });
    const [savingModal, setSavingModal] = useState(false);


    useEffect(() => {
        fetchDropdownData();
        if (project) {
            fetchProjectDetail(project.id);
        }
    }, [project]);

    const fetchProjectDetail = async (projectId) => {
        setFetchingProject(true);
        try {
            const response = await api.get(`/projects/${projectId}`);
            if (response.data.success) {
                const p = response.data.data;
                setFormData({
                    project_code: p.project_code || '',
                    project_name: p.project_name || '',
                    project_type_id: p.project_type_id || '',
                    customer_id: p.customer_id || '',
                    work_order_date: p.work_order_date || '',
                    material_submission_date: p.material_submission_date || '',
                    commissioning_date: p.commissioning_date || '',
                    status: (p.status || 'planning').toLowerCase(),
                    priority: (p.priority || 'medium').toLowerCase(),
                    total_sales_value: p.total_sales_value || '',
                    delay_days: p.delay_days || 0,
                    project_summary: p.project_summary || '',
                    site_address: p.site_address || '',
                    city_id: p.city_id || '',
                    state_id: p.state_id || '',
                    country_id: p.country_id || '',
                    contact_number: p.contact_number || '',
                    site_supervisor_id: p.site_supervisor_id || '',
                    project_manager_id: p.project_manager_id || '',
                    site_google_map_link: p.site_google_map_link || '',
                    site_latitude: p.site_latitude || '',
                    site_longitude: p.site_longitude || '',
                    is_design_approval_enable: p.is_design_approval_enable === 1 || p.is_design_approval_enable === true,
                    is_inspection_tracking_enable: p.is_inspection_tracking_enable === 1 || p.is_inspection_tracking_enable === true,
                    is_material_tracking_enable: p.is_material_tracking_enable === 1 || p.is_material_tracking_enable === true,
                    is_client_billing_enable: p.is_client_billing_enable === 1 || p.is_client_billing_enable === true,
                    radius: p.radius || '',
                    radius_unit_id: p.radius_unit_id || '',
                    contract_copy_filepath: p.contract_copy_filepath || '',
                    contract_copy_filename: p.contract_copy_filename || '',
                    technical_specification_filepath: p.technical_specification_filepath || '',
                    technical_specification_filename: p.technical_specification_filename || '',
                    boq_filepath: p.boq_filepath || '',
                    boq_filename: p.boq_filename || '',
                });
            }
        } catch (error) {
            console.error('Error fetching project detail:', error);
            if (error.response && error.response.status === 404) {
                setNotFound(true);
            } else {
                toast.error('Failed to fetch detailed project information');
            }
        } finally {
            setFetchingProject(false);
        }

    };

    useEffect(() => {
        if (formData.country_id) {
            fetchStates(formData.country_id);
        } else {
            setStates([]);
        }
    }, [formData.country_id]);

    useEffect(() => {
        if (formData.state_id) {
            fetchCities(formData.state_id);
        } else {
            setCities([]);
        }
    }, [formData.state_id]);

    const fetchDropdownData = async () => {
        try {
            const [typeRes, custRes, countRes, empRes, unitRes] = await Promise.all([
                api.get('/project-types-dropdown'),
                api.get('/customers-dropdown'),
                api.get('/countries'),
                api.get('/employees-dropdown'),
                api.get('/units-dropdown')
            ]);
            setProjectTypes((typeRes.data.data || []).map(t => ({ value: t.id, label: t.type_name })));
            setCustomers((custRes.data.data || []).map(c => ({
                value: c.id,
                label: `${c.customer_name}`
            })));
            setCountries((countRes.data.data || []).map(c => ({ value: c.id, label: c.name })));
            setEmployees((empRes.data.data || []).map(e => ({ value: e.id, label: e.employee_name })));
            setUnits((unitRes.data.data || []).map(u => ({ value: u.id, label: u.unit_name })));
        } catch (error) {
            console.error('Error fetching dropdown data:', error);
        }
    };

    const fetchStates = async (countryId) => {
        try {
            const res = await api.get(`/states?country_id=${countryId}`);
            setStates((res.data.data || []).map(s => ({ value: s.id, label: s.name })));
        } catch (error) {
            console.error('Error fetching states:', error);
        }
    };

    const fetchCities = async (stateId) => {
        try {
            const res = await api.get(`/cities?state_id=${stateId}`);
            setCities((res.data.data || []).map(c => ({ value: c.id, label: c.name })));
        } catch (error) {
            console.error('Error fetching cities:', error);
        }
    };

    const extractCoords = (url) => {
        if (!url) return null;

        // 1. Handle long URLs with @lat,lng
        const atMatch = url.match(/@(-?\d+\.\d+),(-?\d+\.\d+)/);
        if (atMatch) return { lat: atMatch[1], lng: atMatch[2] };

        // 2. Handle Google Maps internal format !3dlat!4dlng
        const exclamationMatch = url.match(/!3d(-?\d+\.\d+)!4d(-?\d+\.\d+)/);
        if (exclamationMatch) return { lat: exclamationMatch[1], lng: exclamationMatch[2] };

        // 3. Handle URLs with q=lat,lng or ll=lat,lng
        const qMatch = url.match(/[?&](q|ll)=(-?\d+\.\d+),(-?\d+\.\d+)/);
        if (qMatch) return { lat: qMatch[2], lng: qMatch[3] };

        // 4. Handle "place/Name/lat,lng" format
        const placeMatch = url.match(/place\/[^/]+\/(-?\d+\.\d+),(-?\d+\.\d+)/);
        if (placeMatch) return { lat: placeMatch[1], lng: placeMatch[2] };

        return null;
    };

    const fetchLocationDetails = async () => {
        const link = formData.site_google_map_link;
        if (!link) {
            toast.error("Please enter a Google Maps link first");
            return;
        }

        setLoading(true);
        try {
            let coords = extractCoords(link);

            // If it's a short link and no coords found, resolve it via backend
            if (!coords && (link.includes('goo.gl/maps') || link.includes('maps.app.goo.gl'))) {
                try {
                    const resolveRes = await api.post('/resolve-google-maps-link', { url: link });
                    if (resolveRes.data.success) {
                        if (resolveRes.data.lat && resolveRes.data.lng) {
                            coords = { lat: resolveRes.data.lat, lng: resolveRes.data.lng };
                        } else if (resolveRes.data.resolved_url) {
                            coords = extractCoords(resolveRes.data.resolved_url);
                        }
                    }
                } catch (err) {
                    console.error("Link resolution failed:", err);
                }
            }

            if (!coords) {
                toast.error("Could not extract coordinates from the link. Please use a long Google Maps link.");
                setLoading(false);
                return;
            }

            setFormData(prev => ({
                ...prev,
                site_latitude: coords.lat,
                site_longitude: coords.lng
            }));

            // Reverse Geocoding via Nominatim
            const geoRes = await axios.get(`https://nominatim.openstreetmap.org/reverse`, {
                params: {
                    format: 'json',
                    lat: coords.lat,
                    lon: coords.lng,
                    zoom: 18,
                    addressdetails: 1
                }
            });

            if (geoRes.data && geoRes.data.address) {
                const addr = geoRes.data.address;
                const countryName = addr.country;
                const stateName = addr.state;
                const cityName = addr.city || addr.town || addr.village || addr.suburb || addr.county;

                // Match with our dropdown lists
                const foundCountry = countries.find(c => c.label.toLowerCase() === countryName.toLowerCase());
                if (foundCountry) {
                    setFormData(prev => ({ ...prev, country_id: foundCountry.value }));
                    
                    // Since states are fetched based on country_id, we need to wait or fetch them manually
                    const stateRes = await api.get(`/states?country_id=${foundCountry.value}`);
                    const fetchedStates = (stateRes.data.data || []).map(s => ({ value: s.id, label: s.name }));
                    setStates(fetchedStates);

                    const foundState = fetchedStates.find(s => s.label.toLowerCase() === stateName.toLowerCase());
                    if (foundState) {
                        setFormData(prev => ({ ...prev, state_id: foundState.value }));

                        const cityRes = await api.get(`/cities?state_id=${foundState.value}`);
                        const fetchedCities = (cityRes.data.data || []).map(c => ({ value: c.id, label: c.name }));
                        setCities(fetchedCities);

                        const foundCity = fetchedCities.find(c => c.label.toLowerCase() === cityName.toLowerCase());
                        if (foundCity) {
                            setFormData(prev => ({ ...prev, city_id: foundCity.value }));
                        }
                    }
                }
                toast.success("Location details fetched successfully!");
            }
        } catch (error) {
            console.error("Error fetching location details:", error);
            toast.error("Failed to fetch address details from coordinates");
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        let val = type === 'checkbox' ? checked : value;

        if (name === 'contact_number') {
            val = value.replace(/\D/g, '');
        }

        setFormData(prev => ({
            ...prev,
            [name]: val
        }));
    };

    const handleDateChange = (name, date) => {
        setFormData(prev => ({
            ...prev,
            [name]: date ? date.toLocaleDateString('en-CA') : ''
        }));
    };

    const handleFileChange = (e) => {
        const { name, files: selectedFiles } = e.target;
        if (selectedFiles && selectedFiles[0]) {
            setFiles(prev => ({
                ...prev,
                [name]: selectedFiles[0]
            }));
        }
    };

    const handleSubmit = async (e, shouldAddAnother = false) => {
        if (e) e.preventDefault();
        setLoading(true);
        try {
            const fd = new FormData();

            Object.keys(formData).forEach(key => {
                if (typeof formData[key] === 'boolean') {
                    fd.append(key, formData[key] ? '1' : '0');
                } else {
                    fd.append(key, formData[key] !== null ? formData[key] : '');
                }
            });

            if (files.contract_copy) fd.append('contract_copy', files.contract_copy);
            if (files.technical_specification) fd.append('technical_specification', files.technical_specification);
            if (files.boq) fd.append('boq', files.boq);

            let response;
            if (project) {
                fd.append('_method', 'PUT');
                response = await api.post(`/projects/${project.id}`, fd, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
            } else {
                response = await api.post('/projects', fd, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
            }

            if (response.data.success) {
                toast.success(project ? 'Project updated successfully' : 'Project created successfully');
                onSave(shouldAddAnother);
            }
        } catch (error) {
            console.error('Error saving project:', error);
            toast.error(error.response?.data?.message || 'Failed to save project');
        } finally {
            setLoading(false);
        }
    };

    if (fetchingProject) {
        return (
            <div className={styles['loading-state']}>
                <Loader2 className={styles['spinner']} size={48} />
                <p>Loading project details...</p>
            </div>
        );
    }

    if (notFound) {
        return <NotFound />;
    }

    const handleQuickAddProjectType = () => {
        setIsProjectTypeModalOpen(true);
    };

    const handleSaveProjectType = async (e) => {
        e.preventDefault();
        if (!newProjectType.type_name) {
            toast.error("Project Type name is required");
            return;
        }
        setSavingModal(true);
        try {
            const res = await api.post('/project-types', newProjectType);
            if (res.data.success) {
                const newType = { value: res.data.data.id, label: res.data.data.type_name };
                setProjectTypes(prev => [...prev, newType]);
                setFormData(prev => ({ ...prev, project_type_id: newType.value }));
                toast.success('Project type added successfully');
                setIsProjectTypeModalOpen(false);
                setNewProjectType({ type_name: '' });
            }
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to add project type');
        } finally {
            setSavingModal(false);
        }
    };

    const handleQuickAddCustomer = () => {
        setIsCustomerModalOpen(true);
    };

    const handleSaveCustomer = async (e) => {
        e.preventDefault();
        if (!newCustomer.customer_name) {
            toast.error("Customer name is required");
            return;
        }
        setSavingModal(true);
        try {
            const res = await api.post('/customers', newCustomer);
            if (res.data.success) {
                const newCust = { value: res.data.data.id, label: res.data.data.customer_name };
                setCustomers(prev => [...prev, newCust]);
                setFormData(prev => ({ ...prev, customer_id: newCust.value }));
                toast.success('Customer added successfully');
                setIsCustomerModalOpen(false);
                setNewCustomer({ customer_name: '', mobile: '', address: '', gst: '' });
            }
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to add customer');
        } finally {
            setSavingModal(false);
        }
    };

    return (
        <>
        <form onSubmit={handleSubmit} className={styles['project-form']}>
            <div className={styles['form-sections-grid']}>
                {/* Left Column */}
                <div className={styles['form-column']}>
                    {/* Section 1: Basic Info */}
                    <div className={styles['form-card']}>
                        <div className={styles['form-card-header']}>
                            <Info size={18} className={styles['text-orange']} />
                            <h3>Basic Information</h3>
                        </div>
                        <div className={styles['card-body']}>
                            <div className={styles['form-group']}>
                                <label>Project Code <span className={styles['required-star']}>*</span></label>
                                <div className={styles['input-with-icon']}>
                                    <Hash size={16} />
                                    <input
                                        type="text"
                                        name="project_code"
                                        value={formData.project_code}
                                        onChange={handleChange}
                                        placeholder="e.g. PRJ-2024-001"
                                        required
                                    />
                                </div>
                            </div>
                            <div className={styles['form-group']}>
                                <label>Project Name <span className={styles['required-star']}>*</span></label>
                                <div className={styles['input-with-icon']}>
                                    <FileText size={16} />
                                    <input
                                        type="text"
                                        name="project_name"
                                        value={formData.project_name}
                                        onChange={handleChange}
                                        placeholder="Enter project name"
                                        required
                                    />
                                </div>
                            </div>
                            <div className={styles['form-grid-2']}>
                                <div className={styles['form-group']}>
                                    <label>Project Type <span className={styles['required-star']}>*</span></label>
                                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                        <div style={{ flex: 1 }}>
                                            <CustomSelect
                                                name="project_type_id"
                                                options={projectTypes}
                                                value={projectTypes.find(opt => Number(opt.value) === Number(formData.project_type_id))}
                                                onChange={(option) => setFormData(prev => ({ ...prev, project_type_id: option.value }))}
                                                placeholder="Select Type..."
                                                required
                                            />
                                        </div>
                                        <button 
                                            type="button" 
                                            onClick={handleQuickAddProjectType}
                                            style={{ padding: '8px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '6px', color: '#0f172a', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                            title="Add New Project Type"
                                        >
                                            <Plus size={18} />
                                        </button>
                                    </div>
                                </div>
                                <div className={styles['form-group']}>
                                    <label>Customer <span className={styles['required-star']}>*</span></label>
                                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                        <div style={{ flex: 1 }}>
                                            <CustomSelect
                                                name="customer_id"
                                                value={customers.find(c => Number(c.value) === Number(formData.customer_id))}
                                                onChange={(option) => setFormData(prev => ({ ...prev, customer_id: option ? option.value : '' }))}
                                                options={customers}
                                                placeholder="Select Customer"
                                                isSearchable
                                                required
                                            />
                                        </div>
                                        <button 
                                            type="button" 
                                            onClick={handleQuickAddCustomer}
                                            style={{ padding: '8px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '6px', color: '#0f172a', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                            title="Add New Customer"
                                        >
                                            <Plus size={18} />
                                        </button>
                                    </div>
                                </div>
                            </div>
                            <div className={styles['form-group']}>
                                <label>Project Summary</label>
                                <textarea
                                    name="project_summary"
                                    value={formData.project_summary}
                                    onChange={handleChange}
                                    placeholder="Brief description of the project"
                                    rows={3}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Section 2: Timeline & Sales */}
                    <div className={styles['form-card']}>
                        <div className={styles['form-card-header']}>
                            <Calendar size={18} className={styles['text-orange']} />
                            <h3>Timeline & Financials</h3>
                        </div>
                        <div className={styles['card-body']}>
                            <div className={styles['form-grid-3']}>
                                <div className={styles['form-group']}>
                                    <label>Work Order Date <span className={styles['required-star']}>*</span></label>
                                    <CustomDatePicker
                                        selected={formData.work_order_date}
                                        onChange={(date) => handleDateChange('work_order_date', date)}
                                        required
                                    />
                                </div>
                                <div className={styles['form-group']}>
                                    <label>Material Sub. Date <span className={styles['required-star']}>*</span></label>
                                    <CustomDatePicker
                                        selected={formData.material_submission_date}
                                        onChange={(date) => handleDateChange('material_submission_date', date)}
                                        required
                                    />
                                </div>
                                <div className={styles['form-group']}>
                                    <label>Commissioning Date <span className={styles['required-star']}>*</span></label>
                                    <CustomDatePicker
                                        selected={formData.commissioning_date}
                                        onChange={(date) => handleDateChange('commissioning_date', date)}
                                        required
                                    />
                                </div>
                            </div>
                            <div className={styles['form-grid-2']}>
                                <div className={styles['form-group']}>
                                    <label>Sales Value <span className={styles['required-star']}>*</span></label>
                                    <div className={styles['input-with-icon']}>
                                        <IndianRupee size={16} />
                                        <input
                                            type="number"
                                            name="total_sales_value"
                                            value={formData.total_sales_value}
                                            onChange={handleChange}
                                            placeholder="0.00"
                                            required
                                        />
                                    </div>
                                </div>
                                <div className={styles['form-group']}>
                                    <label>Delay Days</label>
                                    <div className={styles['input-with-icon']}>
                                        <Clock size={16} />
                                        <input type="number" name="delay_days" value={formData.delay_days} onChange={handleChange} />
                                    </div>
                                </div>
                            </div>
                            <div className={styles['form-grid-2']}>
                                <div className={styles['form-group']}>
                                    <label>Status</label>
                                    <select name="status" value={formData.status} onChange={handleChange}>
                                        <option value="planning">Planning</option>
                                        <option value="execution">Execution</option>
                                        <option value="delayed">Delayed</option>
                                        <option value="completed">Completed</option>
                                    </select>
                                </div>
                                <div className={styles['form-group']}>
                                    <label>Priority</label>
                                    <div className={styles['priority-tabs']}>
                                        {['low', 'medium', 'high'].map(p => (
                                            <button
                                                key={p}
                                                type="button"
                                                className={`${styles['priority-tab']} ${styles[p]} ${formData.priority === p ? styles['active'] : ''}`}
                                                onClick={() => setFormData(prev => ({ ...prev, priority: p }))}
                                            >
                                                {p === 'low' && <ArrowDownRight size={16} />}
                                                {p === 'medium' && <Minus size={16} />}
                                                {p === 'high' && <ArrowUpRight size={16} />}
                                                <span>{p.charAt(0).toUpperCase() + p.slice(1)}</span>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                {/* End Left Column */}

                {/* Right Column */}
                <div className={styles['form-column']}>
                    {/* Section 4: Team & Documents */}
                    <div className={styles['form-card']}>
                        <div className={styles['form-card-header']}>
                            <Shield size={18} className={styles['text-orange']} />
                            <h3>Team & Documents</h3>
                        </div>
                        <div className={styles['card-body']}>
                            <div className={styles['form-group']}>
                                <label>Project Manager</label>
                                <CustomSelect
                                    name="project_manager_id"
                                    options={employees}
                                    value={employees.find(opt => Number(opt.value) === Number(formData.project_manager_id))}
                                    onChange={(option) => setFormData(prev => ({ ...prev, project_manager_id: option?.value || '' }))}
                                    placeholder="Assign Manager"
                                    isClearable
                                />
                            </div>
                            <div className={styles['form-group']}>
                                <label>Site Supervisor</label>
                                <CustomSelect
                                    name="site_supervisor_id"
                                    options={employees}
                                    value={employees.find(opt => Number(opt.value) === Number(formData.site_supervisor_id))}
                                    onChange={(option) => setFormData(prev => ({ ...prev, site_supervisor_id: option?.value || '' }))}
                                    placeholder="Assign Supervisor"
                                    isClearable
                                />
                            </div>

                            <div className={styles['section-divider']}>
                                <span>Feature Modules</span>
                            </div>
                            <div className={styles['features-stack']}>
                                {[
                                    { id: 'is_design_approval_enable', label: 'Design Approval', desc: 'Engineering submission flow' },
                                    { id: 'is_inspection_tracking_enable', label: 'Inspection Tracking', desc: 'Quality assurance checks' },
                                    { id: 'is_material_tracking_enable', label: 'Material Tracking', desc: 'Inventory & receipt logs' },
                                    { id: 'is_client_billing_enable', label: 'Client Billing', desc: 'Invoice & payment tracking' },
                                ].map(feature => (
                                    <div
                                        key={feature.id}
                                        className={`${styles['checklist-item']} ${formData[feature.id] ? styles['checked'] : ''}`}
                                        onClick={() => setFormData(prev => ({ ...prev, [feature.id]: !prev[feature.id] }))}
                                    >
                                        <div className={styles['check-box']}>{formData[feature.id] && <Check size={16} strokeWidth={3} />}</div>
                                        <div className={styles['feature-info']}>
                                            <span className={styles['feature-name']}>{feature.label}</span>
                                            <span className={styles['feature-desc']}>{feature.desc}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <div className={styles['section-divider']}>
                                <span>Project Documents</span>
                            </div>
                            <div className={styles['documents-stack']}>
                                {[
                                    { id: 'contract_copy', label: 'Contract Copy', fileKey: 'contract_copy_filename', pathKey: 'contract_copy_filepath' },
                                    { id: 'technical_specification', label: 'Tech. Spec', fileKey: 'technical_specification_filename', pathKey: 'technical_specification_filepath' },
                                    { id: 'boq', label: 'BOQ Document', fileKey: 'boq_filename', pathKey: 'boq_filepath' },
                                ].map(doc => (
                                    <div key={doc.id} className={styles['doc-upload-item']} style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '8px', padding: '16px' }}>
                                        <div className={styles['doc-label-area']}>
                                            <FileText size={16} />
                                            <span className={styles['doc-label-text']}>{doc.label}</span>
                                        </div>
                                        <div className={styles['doc-value-area']} style={{ width: '100%', justifyContent: 'space-between' }}>
                                            <span className={styles['current-filename']} style={{ maxWidth: '200px' }}>
                                                {files[doc.id] ? files[doc.id].name : (formData[doc.fileKey] || 'No file selected')}
                                            </span>
                                            <div className={styles['doc-actions']}>
                                                <label className={styles['upload-btn-mini']}>
                                                    <Upload size={14} />
                                                    <input type="file" name={doc.id} onChange={handleFileChange} />
                                                </label>
                                                {formData[doc.pathKey] && (
                                                    <a href={`${process.env.REACT_APP_API_URL || ''}/storage/${formData[doc.pathKey]}`} target="_blank" rel="noopener noreferrer" className={styles['view-btn-mini']}>
                                                        <ChevronRight size={14} />
                                                    </a>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
                {/* End Right Column */}
            </div>

            {/* Section 3: Site & Geolocation (Full Width) */}
            <div className={styles['form-card']}>
                <div className={styles['form-card-header']}>
                    <MapPin size={18} className={styles['text-orange']} />
                    <h3>Site & Location Details</h3>
                </div>
                <div className={styles['card-body']}>
                    <div className={styles['form-grid-2']}>
                        <div className={styles['form-group']}>
                            <label>Site Address</label>
                            <textarea name="site_address" value={formData.site_address} onChange={handleChange} placeholder="Full site address" rows={2} />
                        </div>
                        <div className={styles['form-group']}>
                            <label>Google Maps Link</label>
                            <div className={styles['input-group-with-btn']}>
                                <div className={styles['input-with-icon']}>
                                    <Navigation size={16} />
                                    <input
                                        type="text"
                                        name="site_google_map_link"
                                        value={formData.site_google_map_link}
                                        onChange={handleChange}
                                        placeholder="https://maps.google.com/..."
                                    />
                                </div>
                                <button
                                    type="button"
                                    className={styles['btn-fetch']}
                                    onClick={fetchLocationDetails}
                                    disabled={loading || !formData.site_google_map_link}
                                    title="Fetch Country, State, City and Coordinates"
                                >
                                    {loading ? <Loader2 size={16} className={styles['spinner']} /> : <Globe size={16} />}
                                    <span>Fetch Info</span>
                                </button>
                            </div>
                        </div>
                    </div>
                    <div className={styles['form-grid-2']}>
                        <div className={styles['form-group']}>
                            <label>Country</label>
                            <CustomSelect
                                name="country_id"
                                options={countries}
                                value={countries.find(opt => Number(opt.value) === Number(formData.country_id))}
                                onChange={(option) => setFormData(prev => ({
                                    ...prev,
                                    country_id: option?.value || '',
                                    state_id: '',
                                    city_id: ''
                                }))}
                                placeholder="Select Country"
                                isClearable
                            />
                        </div>
                        <div className={styles['form-group']}>
                            <label>State</label>
                            <CustomSelect
                                name="state_id"
                                options={states}
                                value={states.find(opt => Number(opt.value) === Number(formData.state_id))}
                                onChange={(option) => setFormData(prev => ({ ...prev, state_id: option?.value || '', city_id: '' }))}
                                placeholder="Select State"
                                isDisabled={!formData.country_id}
                                isClearable
                            />
                        </div>
                    </div>
                    <div className={styles['form-grid-2']}>
                        <div className={styles['form-group']} style={{ marginBottom: 0 }}>
                            <label>City</label>
                            <CustomSelect
                                name="city_id"
                                options={cities}
                                value={cities.find(opt => Number(opt.value) === Number(formData.city_id))}
                                onChange={(option) => setFormData(prev => ({ ...prev, city_id: option?.value || '' }))}
                                placeholder="Select City"
                                isDisabled={!formData.state_id}
                                isClearable
                            />
                        </div>
                        <div className={styles['form-group']} style={{ marginBottom: 0 }}>
                            <label>Contact Number</label>
                            <input type="text" name="contact_number" value={formData.contact_number} onChange={handleChange} placeholder="e.g. 9876543210" />
                        </div>
                    </div>
                    <div className={styles['section-divider']}>
                        <span>Geolocation & Radius</span>
                    </div>
                    {/* <div className={styles['geolocation-section']}> */}
                    <div className={styles['form-grid-2']}>
                        <div className={styles['form-group']}>
                            <label>Latitude</label>
                            <input type="text" name="site_latitude" value={formData.site_latitude} onChange={handleChange} placeholder="e.g. 23.0225" />
                        </div>
                        <div className={styles['form-group']}>
                            <label>Longitude</label>
                            <input type="text" name="site_longitude" value={formData.site_longitude} onChange={handleChange} placeholder="e.g. 72.5714" />
                        </div>
                    </div>
                    <div className={styles['form-grid-2']}>
                        <div className={styles['form-group']}>
                            <label>Tracking Radius</label>
                            <input type="number" name="radius" value={formData.radius} onChange={handleChange} placeholder="Attendance geo-fence radius" />
                        </div>
                        <div className={styles['form-group']}>
                            <label>Radius Unit</label>
                            <CustomSelect
                                name="radius_unit_id"
                                options={units}
                                value={units.find(opt => Number(opt.value) === Number(formData.radius_unit_id))}
                                onChange={(option) => setFormData(prev => ({ ...prev, radius_unit_id: option ? option.value : '' }))}
                                placeholder="Select Unit..."
                            />
                        </div>
                    </div>
                    {/* </div> */}
                </div>
            </div>

            <div className={styles['header-actions-btns']}>
                <button type="button" className={styles['btn-cancel']} onClick={onCancel} disabled={loading}>
                    Cancel
                </button>
                {!project && (
                    <button type="button" className={styles['btn-draft']} onClick={(e) => handleSubmit(e, true)} disabled={loading}>
                        {loading ? <Loader2 className={styles['spinner']} size={18} /> : <Save size={18} />}
                        <span>Save & Add Another</span>
                    </button>
                )}
                <button type="submit" className={styles['btn-primary']} disabled={loading}>
                    {loading ? <Loader2 className={styles['spinner']} size={18} /> : <CheckCircle2 size={18} />}
                    <span>{project ? 'Update Project' : 'Save Project'}</span>
                </button>
            </div>
        </form >

        {isProjectTypeModalOpen && (
            <div className={styles['modal-overlay']} onClick={() => setIsProjectTypeModalOpen(false)}>
                <div className={styles['modal-content']} onClick={(e) => e.stopPropagation()}>
                    <div className={styles['modal-header']}>
                        <h3>Add Project Type</h3>
                        <button className={styles['close-btn']} onClick={() => setIsProjectTypeModalOpen(false)}>
                            <X size={20} />
                        </button>
                    </div>
                    <form onSubmit={handleSaveProjectType}>
                        <div className={styles['modal-body']}>
                            <div className={styles['import-field-group']}>
                                <label>Project Type Name <span className={styles['required-star']}>*</span></label>
                                <input
                                    type="text"
                                    value={newProjectType.type_name}
                                    onChange={(e) => setNewProjectType({ ...newProjectType, type_name: e.target.value })}
                                    placeholder="e.g. Residential, Commercial"
                                    required
                                    style={{ width: '100%', padding: '10px 14px', borderRadius: '10px', border: '1px solid #e2e8f0' }}
                                />
                            </div>
                        </div>
                        <div className={styles['modal-footer']}>
                            <button type="button" className={styles['btn-cancel']} onClick={() => setIsProjectTypeModalOpen(false)} disabled={savingModal}>
                                Cancel
                            </button>
                            <button type="submit" className={styles['btn-upload']} disabled={savingModal}>
                                {savingModal ? <Loader2 size={18} className={styles['spinner']} /> : <Save size={18} />}
                                <span>Save</span>
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        )}

        {isCustomerModalOpen && (
            <div className={styles['modal-overlay']} onClick={() => setIsCustomerModalOpen(false)}>
                <div className={styles['modal-content']} onClick={(e) => e.stopPropagation()}>
                    <div className={styles['modal-header']}>
                        <h3>Add Customer</h3>
                        <button className={styles['close-btn']} onClick={() => setIsCustomerModalOpen(false)}>
                            <X size={20} />
                        </button>
                    </div>
                    <form onSubmit={handleSaveCustomer}>
                        <div className={styles['modal-body']}>
                            <div className={styles['import-field-group']}>
                                <label>Customer Name <span className={styles['required-star']}>*</span></label>
                                <input
                                    type="text"
                                    value={newCustomer.customer_name}
                                    onChange={(e) => setNewCustomer({ ...newCustomer, customer_name: e.target.value })}
                                    placeholder="Enter customer name"
                                    required
                                    style={{ width: '100%', padding: '10px 14px', borderRadius: '10px', border: '1px solid #e2e8f0' }}
                                />
                            </div>
                            <div className={styles['import-field-group']}>
                                <label>Mobile Number</label>
                                <input
                                    type="text"
                                    value={newCustomer.mobile}
                                    onChange={(e) => setNewCustomer({ ...newCustomer, mobile: e.target.value })}
                                    placeholder="Enter mobile number"
                                    style={{ width: '100%', padding: '10px 14px', borderRadius: '10px', border: '1px solid #e2e8f0' }}
                                />
                            </div>
                            <div className={styles['import-field-group']}>
                                <label>GST Number</label>
                                <input
                                    type="text"
                                    value={newCustomer.gst}
                                    onChange={(e) => setNewCustomer({ ...newCustomer, gst: e.target.value })}
                                    placeholder="Enter GST number"
                                    style={{ width: '100%', padding: '10px 14px', borderRadius: '10px', border: '1px solid #e2e8f0' }}
                                />
                            </div>
                            <div className={styles['import-field-group']}>
                                <label>Address</label>
                                <textarea
                                    value={newCustomer.address}
                                    onChange={(e) => setNewCustomer({ ...newCustomer, address: e.target.value })}
                                    placeholder="Enter complete address"
                                    rows={2}
                                    style={{ width: '100%', padding: '10px 14px', borderRadius: '10px', border: '1px solid #e2e8f0', fontFamily: 'inherit' }}
                                />
                            </div>
                        </div>
                        <div className={styles['modal-footer']}>
                            <button type="button" className={styles['btn-cancel']} onClick={() => setIsCustomerModalOpen(false)} disabled={savingModal}>
                                Cancel
                            </button>
                            <button type="submit" className={styles['btn-upload']} disabled={savingModal}>
                                {savingModal ? <Loader2 size={18} className={styles['spinner']} /> : <Save size={18} />}
                                <span>Save</span>
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        )}
        </>
    );
};

export default ProjectForm;
